import { useCallback } from 'react'
import { useChainId } from 'wagmi'

import { UseSavingsGeneralStatsResult, useSavingsGeneralStats } from '../data/useGeneralStats'
import { AccountDefinition, AccountsNavigationProps, StusdsWarningData } from '../types'
import { getInterestData } from './getInterestData'
import { makeAccountDefinition } from './makeAccountDefinition'
import { makeMigrationInfo } from './makeMigrationInfo'
import { makeNavigationProps } from './makeNavigationProps'
import { usePrefetchSavingsTimestamps } from './usePrefetchSavingsTimestamps'
import { usePrefetchValidators } from './usePrefetchValidators'
import { useSelectedAccount } from './useSelectedAccount'
import { useSyncUrl } from './useSyncUrl'
import { tutorialDialogConfig } from '@/base-miniapp/tutorial-dialog/TutorialDialog'
import { BaseMiniAppOptions } from '@/base-miniapp/types'
import { getChainConfigEntry } from '@/config/chain'
import { useGetBlockExplorerAddressLink } from '@/domain/hooks/useGetBlockExplorerAddressLink'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { usePrefetchStusdsData } from '@/features/savings/data/usePrefetchStusdsData'
import { useSparkRewardsSummary } from '@/features/savings/data/useSparkRewardsSummary'
import { useStusdsWarningData } from '@/features/savings/data/useStusdsWarningData'
import { convertStablesDialogConfig } from '@/features/savings/dialogs/convert-stables/ConvertStablesDialog'
import { savingsDepositDialogConfig } from '@/features/savings/dialogs/deposit/SavingsDepositDialog'
import { useOpenStusdsWelcomeDialog } from '@/features/savings/dialogs/welcome-stusds/logic/useOpenStusdsWelcomeDialog'
import { savingsWithdrawDialogConfig } from '@/features/savings/dialogs/withdraw/SavingsWithdrawDialog'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'
import { UseSavingsChartsResult, useSavingsCharts } from '@/features/savings/logic/useSavingsCharts'
import { useTimestamp } from '@/utils/useTimestamp'

export interface UseSavingsResults {
  headerChainId: number | undefined
  navigationProps: AccountsNavigationProps
  selectedAccount: AccountDefinition
  generalStats: UseSavingsGeneralStatsResult
  openDepositDialog: (tokenToDeposit: Token) => void
  openSendDialog: () => void
  openWithdrawDialog: () => void
  openConvertStablesDialog: () => void
  isInSandbox: boolean
  charts: UseSavingsChartsResult
  stusdsWarningData: StusdsWarningData | undefined
  baseMiniAppOptions: BaseMiniAppOptions | undefined
}

export function useSavings(): UseSavingsResults {
  const wagmiChainId = useChainId()
  const { savingsTokenSymbol: selectedSavingsTokenSymbol, chainId } = useSelectedAccount()
  const chainConfig = getChainConfigEntry(chainId)
  const { savings, nativeAssetInfo } = chainConfig
  const savingsAccounts = useSavingsAccountRepository({ chainId })
  const { timestamp } = useTimestamp()
  const openDialog = useOpenDialog()
  const getBlockExplorerLink = useGetBlockExplorerAddressLink()
  const { isInSandbox } = useSandbox()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })

  const savingsTokenRepository = useSavingsTokenRepository({ chainId, account })

  usePrefetchValidators({ chainId, tokenRepository: savingsTokenRepository, savingsAccounts })
  usePrefetchSavingsTimestamps({ chainId })

  const selectedAccount = savingsAccounts.findOneBySavingsTokenSymbol(selectedSavingsTokenSymbol)
  const selectedAccountConfig = savings?.accounts?.find(
    ({ savingsToken }) => savingsToken === selectedSavingsTokenSymbol,
  )

  useSyncUrl({ pageChainId: chainId })

  const stusdsWarningData = useStusdsWarningData({ enabled: selectedSavingsTokenSymbol === TokenSymbol('stUSDS') })

  const sparkRewardsSummary = useSparkRewardsSummary({
    chainId,
    savingsToken: selectedAccount.savingsToken,
  })

  const sparkPointsSummary = {
    multiplier: selectedAccountConfig?.sparkPointsMultiplier ?? 0,
  }

  const migrationInfo = makeMigrationInfo({
    selectedAccount: selectedSavingsTokenSymbol,
    savingsAccounts,
    openDialog,
  })

  useOpenStusdsWelcomeDialog({ enabled: selectedSavingsTokenSymbol === TokenSymbol('stUSDS'), chainId })

  const navigationProps = makeNavigationProps({
    chainId,
    savingsAccounts,
    nativeAssetInfo,
    selectedAccount: selectedSavingsTokenSymbol,
    tokenRepository: savingsTokenRepository,
  })

  usePrefetchStusdsData({
    chainId,
    enabled: navigationProps.opportunityAccounts.length > 0,
  })

  const interestData = getInterestData({
    savingsConverter: selectedAccount.converter,
    savingsTokenBalance: savingsTokenRepository.findOneBalanceOrZero(selectedAccount.savingsToken.symbol),
    underlyingToken: savingsTokenRepository.findOneBySymbol(selectedAccount.underlyingToken.symbol),
    sparkRewardsSummary,
    timestamp,
  })

  const generalStats = useSavingsGeneralStats({
    selectedAccountConfig,
    chainId,
  })
  const charts = useSavingsCharts({
    chainId,
    selectedSavingsTokenSymbol,
    savingsToken: selectedAccount.savingsToken,
    selectedAccountConfig,
    liquidityChartImmediateLiquidity: generalStats.queryResult.data?.liquidity,
    liquidityChartSystemAvailableLiquidity: generalStats.queryResult.data?.systemAvailableLiquidity,
  })

  // @note: Memoized for tanstack table
  const openDepositDialog = useCallback(
    (tokenToDeposit: Token) =>
      openDialog(savingsDepositDialogConfig, {
        initialToken: tokenToDeposit,
        chainId,
        savingsToken: selectedAccount.savingsToken,
      }),
    [openDialog, selectedAccount.savingsToken, chainId],
  )

  function openConvertStablesDialog(): void {
    openDialog(convertStablesDialogConfig, { proceedText: 'Back to Savings' })
  }
  function openSendDialog(): void {
    openDialog(savingsWithdrawDialogConfig, {
      mode: 'send' as const,
      chainId,
      savingsToken: selectedAccount.savingsToken,
    })
  }
  function openWithdrawDialog(): void {
    openDialog(savingsWithdrawDialogConfig, {
      mode: 'withdraw' as const,
      chainId,
      savingsToken: selectedAccount.savingsToken,
    })
  }

  const selectedAccountDefinition = makeAccountDefinition({
    selectedAccount,
    interestData,
    selectedSavingsTokenSymbol,
    chainId,
    getBlockExplorerLink,
    migrationInfo,
    sparkRewardsSummary,
    sparkPointsSummary,
    savingsTokenRepository,
  })

  return {
    headerChainId: wagmiChainId === chainId ? undefined : chainId,
    navigationProps,
    generalStats,
    openDepositDialog,
    openSendDialog,
    openWithdrawDialog,
    openConvertStablesDialog,
    isInSandbox,
    charts,
    selectedAccount: selectedAccountDefinition,
    stusdsWarningData,
    baseMiniAppOptions:
      import.meta.env.VITE_BASE_MINI_APP === '1'
        ? {
            openTutorialDialog: () => {
              openDialog(tutorialDialogConfig, {})
            },
          }
        : undefined,
  }
}
